import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { Switch as MaterialSwitch, Theme, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import { GridRenderCellParams, GridRowData } from '@mui/x-data-grid';
import { AxiosResponse, CancelTokenSource, Method } from 'axios';
import clsx from 'clsx';
import { OptionsObject, SnackbarKey } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, UseMutationResult } from 'react-query';
import { NavLink, Route, Switch, useHistory } from 'react-router-dom';
import {
  SetterOrUpdater,
  useRecoilStateLoadable,
  useRecoilValueLoadable,
} from 'recoil';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import { trackAddNewInboundRoutingRule } from '../../common/userTracking';
import { IUserAtom, user } from '../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { DeleteRoutingRuleModal } from '../components/modals/DeleteRoutingRuleModal';
import InboundCallRoutingModal from '../components/modals/InboundCallRoutingModal';
import {
  IAlertWithProgressBar,
  useSnackbarWithStyles,
} from '../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles((theme: Theme) => ({
  fullWidth: {
    width: '100% !important',
  },
  actionColumn: {
    display: 'row',
    width: '150px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  numbersList: {
    width: '120px',
    padding: '0',
    paddingBottom: '1px',
    listStyleType: 'none',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  numbersList2: {
    width: '80px',
    padding: '0',
    paddingBottom: '1px',
    listStyleType: 'none',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  infoTip: {
    marginTop: 5,
    marginLeft: 15,
    // border: '1px solid #ccc',
    // padding: '6.5px 14px',
    // borderRadius: 5
  },
}));

function onlyUnique(value, index, array) {
  return array.indexOf(value) === index;
}

const CallRoutingToggle = (props: {
  params: any;
  mutation: UseMutationResult<
    AxiosResponse<any>,
    unknown,
    {
      endpoint: string;
      payload?:
        | {
            [key: string]: any;
          }
        | undefined;
      method?: Method | undefined;
      omitBusinessIdAndApikey?: boolean | undefined;
    },
    unknown
  >;
  enqueueSnackbarWithStyles: (
    alertComponentProps: IAlertWithProgressBar,
    snackbarProps?: OptionsObject | undefined
  ) => SnackbarKey | null;
  setRefetchToggleForRoutingRules: SetterOrUpdater<any>;
  userAtom: IUserAtom;
  disabled?: boolean;
}) => {
  const [checked, setChecked] = useState<boolean>(
    Boolean(props.params?.active === 1)
  );
  useEffect(() => {
    setChecked(Boolean(props?.params?.active === 1));
  }, [props?.params?.active]);
  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): Promise<void> => {
    setChecked(checked);
    try {
      const data = {
        call: methodNameEnum.UPDATE_ROUTING_RULE_ACTIVE,
        businessId: props.userAtom?.business?.businessid as any,
        routingId: Number(props?.params?.id),
        active: Number(checked ? 1 : 0),
        apikey: props.userAtom?.apikey,
        name: props?.params?.rule,
      };
      const response = await props.mutation.mutateAsync({
        endpoint: endpointEnum.UPDATE_ROUTING_RULE_ACTIVE,
        payload: data,
      });
      props.setRefetchToggleForRoutingRules({});
      if (response?.data?.success === true) {
        props.enqueueSnackbarWithStyles({
          message: `Success updating routing rule active status`,
          alertProps: { severity: 'success' },
        });
      }
    } catch (err) {
      props.enqueueSnackbarWithStyles({
        message: `Error updating routing rule active status`,
        alertProps: { severity: 'error' },
      });
    }
  };
  return (
    <MaterialSwitch
      checked={checked}
      onChange={handleChange}
      color="primary"
      name="status"
      inputProps={{ 'aria-label': 'primary checkbox' }}
      disabled={props.disabled}
    />
  );
};

const InboundCallRouting = () => {
  // const [page, setPage] = useState<number>(1);
  // const [pageSize, setPageSize] = useState<number>(10);
  const [, setUserAtom] = useRecoilStateLoadable(user);
  const [, setDeleteCancelTokenSource] = useState<
    CancelTokenSource | undefined
  >();
  const [selected, setSelected] = useState<GridRowData[]>([]);
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const history = useHistory();

  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;

  const data = {
    call: methodNameEnum.GET_BUSINESS_NUMBERS,
  };
  const [
    ,
    businessNumbersLoadable,
    // setRefetchToggleForNumbers,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_NUMBERS,
      payloadJsonString: JSON.stringify(data),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.map((o) => {
        return {
          ...o,
          id: o.Numberid,
        };
      }) ?? cached.current
  );

  const [
    _,
    routingRulesLoadable,
    setRefetchToggleForRoutingRules,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_ROUTING_RULES,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_ROUTING_RULES,
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );
  useEffect(() => {
    setRefetchToggleForRoutingRules({});
  }, []);

  const classes = useStyles();

  const dataUsers = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };

  const conferenceData = {
    call: methodNameEnum.GET_BUSINESS_CONFERENCES,
  };

  const dataRingGroups = {
    call: methodNameEnum.GET_BUSINESS_GROUPS,
  };

  const businessUsers = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(dataUsers),
    })
  );

  const businessIvrs = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_IVRS })
  );

  const businessConferences = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_CONFERENCES,
      payloadJsonString: JSON.stringify(conferenceData),
    })
  );

  const businessRingGroups = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_GROUPS,
      payloadJsonString: JSON.stringify(dataRingGroups),
    })
  );

  const businessQueues = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_QUEUES })
  );

  const activeAgents = useMemo(
    () =>
      businessUsers.state === 'hasValue'
        ? [...(businessUsers.contents?.response?.data?.result ?? [])]
            ?.filter((user) => user.isactive === 1)
            ?.sort((a, b) =>
              a.fname.toLowerCase().localeCompare(b.fname.toLowerCase())
            )
            ?.reduce(
              (acc, item) => ({
                ...acc,
                ids: acc.ids.concat(item.userid),
                lookup: {
                  ...acc.lookup,
                  [item.userid]: item,
                },
              }),
              {
                ids: [],
                lookup: {},
              }
            )
        : [],
    [businessUsers]
  );

  const ivrs = useMemo(
    () =>
      businessIvrs.state === 'hasValue'
        ? [...(businessIvrs.contents?.data?.result ?? [])]
            ?.filter((ivr) => ivr.active === 1)
            ?.sort((a, b) =>
              a.IVRname.toLowerCase().localeCompare(b.IVRname.toLowerCase())
            )
            ?.reduce(
              (acc, item) => ({
                ...acc,
                ids: acc.ids.concat(item.mainIVRid),
                lookup: {
                  ...acc.lookup,
                  [item.mainIVRid]: item,
                },
              }),
              {
                ids: [],
                lookup: {},
              }
            )
        : [],
    [businessIvrs]
  );

  const conferences = useMemo(
    () =>
      businessConferences.state === 'hasValue'
        ? [...(businessConferences.contents?.response?.data?.result ?? [])]
            ?.sort((a, b) =>
              a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            )
            ?.reduce(
              (acc, item) => ({
                ...acc,
                ids: acc.ids.concat(item.conferenceid),
                lookup: {
                  ...acc.lookup,
                  [item.conferenceid]: item,
                },
              }),
              {
                ids: [],
                lookup: {},
              }
            )
        : [],
    [businessConferences]
  );

  const groups = useMemo(
    () =>
      businessRingGroups.state === 'hasValue'
        ? [...(businessRingGroups.contents?.response?.data?.result ?? [])]
            ?.sort((a, b) =>
              a.groupname.toLowerCase().localeCompare(b.groupname.toLowerCase())
            )
            ?.reduce(
              (acc, item) => ({
                ...acc,
                ids: acc.ids.concat(item.maingroupid),
                lookup: {
                  ...acc.lookup,
                  [item.maingroupid]: item,
                },
              }),
              {
                ids: [],
                lookup: {},
              }
            )
        : [],
    [businessRingGroups]
  );

  const queues = useMemo(
    () =>
      businessQueues.state === 'hasValue'
        ? [...(businessQueues.contents?.data?.result ?? [])]
            ?.sort((a, b) =>
              a.queuename.toLowerCase().localeCompare(b.queuename.toLowerCase())
            )
            ?.reduce(
              (acc, item) => ({
                ...acc,
                ids: acc.ids.concat(Number(item.queueid)),
                lookup: {
                  ...acc.lookup,
                  [item.queueid]: item,
                },
              }),
              {
                ids: [],
                lookup: {},
              }
            )
        : [],
    [businessQueues]
  );

  const parseRule = (ruleJSON) => {
    if (ruleJSON) {
      try {
        return JSON.parse(ruleJSON);
      } catch (err) {
        enqueueSnackbarWithStyles({
          message: `Encountered an error parsing rule results`,
          alertProps: { severity: 'error' },
        });
      }
    }
  };

  const rows = useMemo(() => {
    if (
      routingRulesLoadable.state === 'hasValue' &&
      businessUsers.state === 'hasValue' &&
      businessIvrs.state === 'hasValue' &&
      businessRingGroups.state === 'hasValue' &&
      businessQueues.state === 'hasValue' &&
      businessConferences.state === 'hasValue' &&
      businessNumbersLoadable.state === 'hasValue'
    ) {
      return routingRulesLoadable?.contents?.response?.data?.result?.map(
        (el, i) => {
          const rule = parseRule(el?.JSON);
          const numsObj = rule?.numbers?.[0];
          let nums: string[] = [];
          let DIDs: any[] = [];
          for (const key in numsObj) {
            nums = nums.concat(
              numsObj[key]
                .filter((el) => el !== null)
                .map((e) => {
                  return key === 'All'
                    ? `${
                        e === 'localPresence'
                          ? 'Connection Boost'
                          : e === 'localPresenceLite'
                          ? 'Connection Boost Pool'
                          : e === 'ivr'
                          ? 'IVR'
                          : e.charAt(0).toUpperCase() + e.slice(1)
                      }: All`
                    : key === 'Agent'
                    ? `Agent: ${activeAgents?.lookup?.[e]?.fname} ${activeAgents?.lookup?.[e]?.lname}`
                    : key === 'IVR'
                    ? `IVR: ${ivrs?.lookup?.[e]?.IVRname}`
                    : key === 'Conference'
                    ? `Conference: ${conferences?.lookup?.[e]?.name ?? ''}`
                    : key === 'Group'
                    ? `Group: ${groups?.lookup?.[e]?.groupname}`
                    : key === 'Queue'
                    ? `Queue: ${queues?.lookup?.[e]?.queuename}`
                    : e;
                })
            );
            try {
              const allEntityType = numsObj.All?.[0];
              let allEntities: any[] = [];
              switch (allEntityType) {
                case 'group':
                  Object.keys(groups?.lookup ?? []).forEach((id) => {
                    try {
                      const numbersInfo = JSON.parse(
                        groups.lookup?.[id]?.numbersInfo ?? '[]'
                      );
                      allEntities = allEntities.concat(numbersInfo);
                    } catch (e) {
                      //
                    }
                  });
                  break;
                case 'conference':
                  Object.keys(conferences?.lookup ?? []).forEach((id) => {
                    try {
                      const numbersInfo = JSON.parse(
                        conferences.lookup?.[id]?.numbersInfo ?? '[]'
                      );
                      allEntities = allEntities.concat(numbersInfo);
                    } catch (e) {
                      //
                    }
                  });
                  break;
                case 'queue':
                  Object.keys(queues?.lookup ?? []).forEach((id) => {
                    try {
                      const numbersInfo = JSON.parse(
                        queues.lookup?.[id]?.numbersInfo ?? '[]'
                      );
                      allEntities = allEntities.concat(numbersInfo);
                    } catch (e) {
                      //
                    }
                  });
                  break;
                case 'agent':
                  Object.keys(activeAgents?.lookup ?? []).forEach((id) => {
                    try {
                      const numbersInfo = JSON.parse(
                        activeAgents.lookup?.[id]?.numbersInfo ?? '[]'
                      );
                      allEntities = allEntities.concat(numbersInfo);
                    } catch (e) {
                      //
                    }
                  });
                  break;
                case 'ivr':
                  Object.keys(ivrs?.lookup ?? []).forEach((id) => {
                    try {
                      const numbersInfo = JSON.parse(
                        ivrs.lookup?.[id]?.numbersInfo ?? '[]'
                      );
                      allEntities = allEntities.concat(numbersInfo);
                    } catch (e) {
                      //
                    }
                  });
                  break;
              }
              allEntities = (allEntities ?? []).filter(
                (entity) => !!entity.DID
              );
              const allEntitiesDIDs = allEntities.map((entity) => entity.DID);
              numsObj[key]
                .filter((el) => el !== null)
                .forEach((e) => {
                  DIDs = DIDs.concat(
                    key === 'All'
                      ? allEntitiesDIDs
                      : key === 'Agent'
                      ? JSON.parse(
                          activeAgents?.lookup?.[e]?.numbersInfo ?? '[]'
                        )
                      : key === 'IVR'
                      ? JSON.parse(ivrs?.lookup?.[e]?.numbersInfo ?? '[]')
                      : key === 'Conference'
                      ? JSON.parse(
                          conferences?.lookup?.[e]?.numbersInfo ?? '[]'
                        )
                      : key === 'Group'
                      ? JSON.parse(groups?.lookup?.[e]?.numbersInfo ?? '[]')
                      : key === 'Queue'
                      ? JSON.parse(queues?.lookup?.[e]?.numbersInfo ?? '[]')
                      : key === 'Number'
                      ? numsObj[key]
                      : []
                  );
                });
            } catch (e) {
              // DIDs = DIDs
            }
          }
          DIDs = DIDs.map(
            (numInfo: { Numberid: number | null; DID: string | null }) =>
              !!numInfo?.DID || numInfo?.DID === null ? numInfo.DID : numInfo
          ).filter(onlyUnique);

          // const useAllDIDs = nums?.[0]?.includes?.('All');
          // const allType = useAllDIDs ? nums?.[0]?.split?.(':')?.[0] : '';
          // const allTypeNumbers = (
          //   businessNumbersLoadable?.contents?.response?.data?.result ?? []
          // )
          //   .filter((businessNum) => businessNum?.type === allType)
          //   .map((filteredNum) => filteredNum?.DID);
          // DIDs = allType ? allTypeNumbers : DIDs;

          return {
            check: false,
            order: rule.order,
            rule:
              rule?.name?.split(' ')?.slice(0, 2)?.join(' ') === 'Has Owner'
                ? 'Contact Has Owner ' +
                  rule?.name
                    ?.split(' ')
                    ?.slice(2, rule?.name?.length - 1)
                    .join(' ')
                : rule.name,
            numbers: nums,
            did: DIDs,
            routingLogCounts: rule.routingLogCounts,
            active: rule.isActive,
            id: rule.routingId,
          };
        }
      );
    }
  }, [
    routingRulesLoadable,
    activeAgents,
    businessIvrs,
    businessConferences,
    businessRingGroups,
    businessQueues,
    businessNumbersLoadable,
  ]);

  // const handlePageChange = (page: number, details?: any) => {
  //   setPage(page);
  // };

  // const handlePageSizeChange = (pageSize: number, details?: any) => {
  //   setPageSize(pageSize);
  // };

  useEffect(() => {
    try {
      const updated = JSON.parse(
        localStorage.getItem('originalBusinessObject') ?? '{}'
      );
      if (!updated?.businessid) {
        //
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('filter')) {
          urlParams.delete('filter');
        }
        if (urlParams.has('type')) {
          urlParams.delete('type');
        }
        history.replace({
          search: urlParams.toString(),
        });
      }
    } catch (e) {
      //
    }
  }, [userAtomContents?.businessid]);

  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        isLoading={routingRulesLoadable.state === 'loading'}
        dataType="RoutingRules"
        numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            dataPup={'add-routing-rule'}
            numSelected={selected.length}
            onClickForAdd={() => {
              trackAddNewInboundRoutingRule();
              history.push('/manage/numbers/routing/add');
            }}
            onClickForDelete={() => {
              history.push('/manage/numbers/routing/delete');
            }}
            isLoading={props.isLoading}
          />
        }
        leftComponent={
          <div className={classes.infoTip}>
            Click and drag Rules to reorder
            <Tooltip title="Inbound calls to your business check Rules and Conditions on this page in the order they appear here. Multiple Rules can be associated with the same or different numbers. Click and drag the icon in the Order column to reorder based on your business needs.">
              <InfoIcon
                style={{
                  cursor: 'pointer',
                  marginLeft: 5,
                  marginTop: -3,
                  // color: '#0098C7',
                }}
              />
            </Tooltip>
          </div>
        }
      />
    ),
    [routingRulesLoadable, selected.length]
  );

  const mutation = useMutation(
    getApiMutation(userAtomContents, setUserAtom, setDeleteCancelTokenSource)
  );

  const readOnly = useBusinessPermissions().accountReadOnly;
  // const orderHeaderFormatter = `Order${<Tooltip title="Inbound calls to your business check Rules and Conditions on this page in the order they appear here. Multiple Rules can be associated with the same or different numbers. Click and drag the icon in the Order column to reorder based on your business needs.">ⓘ</Tooltip>}`;

  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'check',
        headerName: ' ',
        hide: readOnly,
        disableExport: true,
        headerAlign: 'center',
        align: 'center',
        type: 'boolean',
        width: 70,
      },
      {
        field: 'order',
        headerName: 'Order',
        hide: readOnly,
        disableExport: true,
        headerAlign: 'center',
        align: 'center',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        width: 64,
      },
      {
        field: 'rule',
        headerName: 'Rule',
        headerAlign: 'left',
        align: 'left',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        width: 200,
      },
      {
        field: 'numbers',
        headerName: 'Name',
        headerAlign: 'left',
        align: 'left',
        width: 200,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        renderCell: (params: GridRenderCellParams<any>) => {
          return (
            <>
              <ul className={classes.numbersList}>
                {params?.numbers?.length > 3 ? (
                  <>
                    {params?.numbers.slice(0, 3).map((number, i) => (
                      <li key={`${number}-${i}`}>{number}</li>
                    ))}
                    <li>
                      <NavLink
                        to={{
                          pathname: `/manage/numbers/routing/${params?.id}/edit`,
                          state: { index: 0 },
                        }}
                      >
                        + {params?.numbers?.length - 3} more ...
                      </NavLink>
                    </li>
                  </>
                ) : (
                  params?.numbers?.map((number, i) => (
                    <li key={`${number}-${i}`}>{number}</li>
                  ))
                )}
              </ul>
            </>
          );
        },
      },
      {
        field: 'did',
        headerName: 'Numbers',
        headerAlign: 'left',
        align: 'left',
        width: 152,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        renderCell: (params: any) => {
          const didString = params?.did?.length ? params.did.join(',') : '-';
          return (
            <>
              <ul className={classes.numbersList}>
                {params?.did?.length > 3 ? (
                  <Tooltip title={didString}>
                    <div>
                      {params.did.slice(0, 3).map((number, i) => (
                        <li key={`${number}-${i}`}>{number}</li>
                      ))}
                      <li>+ {params.did.length - 3} more ...</li>
                    </div>
                  </Tooltip>
                ) : (
                  params.did.map((number, i) => (
                    <li key={`${number}-${i}`}>{number}</li>
                  ))
                )}
              </ul>
            </>
          );
        },
      },
      {
        field: 'routingLogCounts',
        headerName: 'Calls Routed (last 7 days)',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'active',
        headerName: 'Active',
        headerAlign: 'center',
        width: 100,
        align: 'center',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        renderCell: (params) => {
          return (
            <>
              <div className={clsx(classes.actionColumn, 'row')}>
                <CallRoutingToggle
                  params={params}
                  mutation={mutation}
                  enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
                  setRefetchToggleForRoutingRules={
                    setRefetchToggleForRoutingRules
                  }
                  userAtom={userAtomContents}
                />
              </div>
            </>
          );
        },
        disableExport: true,
      },
      {
        field: 'id',
        width: 150,
        headerName: 'Actions',
        hide: readOnly,
        cellClassName: classes.actionColumn,
        disableExport: true,
        disableSearch: true,
        headerAlign: 'center',
        disableRowSelection: true,
        enableSorting: false,
        renderCell: (params) => (
          <div className={clsx(classes.actionColumn, 'row')}>
            <NavLink to={`/manage/numbers/routing/${params}/edit`}>
              <Tooltip title="Edit">
                <IconButton name="edit" size="large">
                  <CreateIcon color="primary" />
                </IconButton>
              </Tooltip>
            </NavLink>
            <NavLink to={`/manage/numbers/routing/${params}/delete`}>
              <Tooltip title="Delete">
                <IconButton
                  data-pup={`delete-routing-rule-id`}
                  name="delete"
                  size="large"
                >
                  <DeleteIcon className={classes.deleteIconButton} />
                </IconButton>
              </Tooltip>
            </NavLink>
          </div>
        ),
      },
    ],
    [classes]
  );

  const handleDeletion = async (id: string | number) => {
    const data = {
      call: methodNameEnum.DELETE_ROUTING_RULE,
      apikey: userAtomContents?.apikey,
      businessid: userAtomContents?.businessid,
      businessId: userAtomContents?.businessid,
      routingId: id,
    };
    const response = await mutation.mutateAsync({
      endpoint: endpointEnum.DELETE_ROUTING_RULE,
      payload: data,
    });
    if (response?.data?.success === false) {
      enqueueSnackbarWithStyles({
        message: `Encountered an error deleting the rule`,
        alertProps: { severity: 'error' },
      });
    } else {
      enqueueSnackbarWithStyles({
        message: `Successfully Deleted Routing Rule`,
        alertProps: { severity: 'success' },
      });
    }
    setRefetchToggleForRoutingRules({});
  };
  const handleMultipleDeletion = () => {
    if (selected.length) {
      for (let i = 0; i < selected.length; i++) {
        handleDeletion(selected[i].id);
      }
    }
  };

  const onDragEndHandler = async (draggedId, currPos, tarPos) => {
    try {
      const data: any = {
        call: methodNameEnum.UPDATE_ROUTING_RULES_ORDER,
        currentPos: currPos,
        targetPos: tarPos,
        routingId: draggedId,
        businessId: userAtomContents?.businessid,
        businessid: userAtomContents?.businessid,
        apikey: userAtomContents?.apikey,
      };
      const response = await mutation.mutateAsync({
        endpoint: endpointEnum.UPDATE_ROUTING_RULES_ORDER,
        payload: data,
      });
      if (response.status === 200) {
        enqueueSnackbarWithStyles({
          message: `Success updating routing rule order`,
          alertProps: { severity: 'success' },
        });
      }
    } catch (err) {
      enqueueSnackbarWithStyles({
        message: `Error updating routing rule order`,
        alertProps: { severity: 'error' },
      });
    }
    setRefetchToggleForRoutingRules({});
  };

  return (
    <>
      <Switch>
        <Route path="/manage/numbers/routing/add">
          {({ history, match }) => (
            <InboundCallRoutingModal
              show={match != null}
              setRefetchToggle={setRefetchToggleForRoutingRules}
              id={undefined}
              groups={groups}
              agents={activeAgents}
              ivrs={ivrs}
              queues={queues}
              conferences={conferences}
              handleClose={() => {
                history.push('/manage/numbers/routing');
              }}
            />
          )}
        </Route>
        <Route path="/manage/numbers/routing/:id/edit">
          {({ history, match }) => (
            <InboundCallRoutingModal
              show={match != null}
              setRefetchToggle={setRefetchToggleForRoutingRules}
              id={match?.params.id}
              groups={groups}
              agents={activeAgents}
              ivrs={ivrs}
              queues={queues}
              conferences={conferences}
              handleClose={() => {
                history.push('/manage/numbers/routing');
              }}
            />
          )}
        </Route>
        <Route path="/manage/numbers/routing/:id/delete">
          {({ history, match }) => (
            <DeleteRoutingRuleModal
              handleDeletion={handleDeletion}
              show={match != null}
              deleteId={match?.params.id}
              handleClose={() => {
                history.push('/manage/numbers/routing');
                setSelected([]);
                setRefetchToggleForRoutingRules({});
              }}
            />
          )}
        </Route>
        <Route path="/manage/numbers/routing/delete">
          {({ history, match }) => (
            <DeleteRoutingRuleModal
              handleDeletion={handleMultipleDeletion}
              show={match != null}
              deleteId={match?.params.id}
              handleClose={() => {
                history.push('/manage/numbers/routing');
                setSelected([]);
                setRefetchToggleForRoutingRules({});
              }}
            />
          )}
        </Route>
      </Switch>
      <DataTable
        rows={rows}
        columns={columns}
        rowCount={
          routingRulesLoadable?.contents?.response?.data?.result?.length ?? 0
        }
        // page={page}
        // pageSize={pageSize}
        // onPageChange={handlePageChange}
        // onPageSizeChange={handlePageSizeChange}
        paginationMode="server"
        loading={routingRulesLoadable.state === 'loading'}
        ToolbarComponent={ToolbarComponent}
        pagination
        draggable={true}
        onDragEnd={onDragEndHandler}
        setDragSelected={(selected: GridRowData[]) => {
          setSelected(selected);
        }}
        selected={selected}
        enableSelectAll={true}
        checkInitialFilter={true}
        additionalArraySearchFields={['did', 'numbers']}
      />
    </>
  );
};

export default InboundCallRouting;
